import type { PbjCategoryScore, PbjPrediction } from "../../../utils/models";
import { bandPointsForValue } from "./bandBuffer.utils";
import type { PlanItem, PlanItemCategoryBreakdown } from "./recommendations.constants";

// Points a `rawValue` earns on the category's band ladder, scored against the EFFECTIVE
// (safety-margin-buffered) bounds — the same ladder the backend scored `cat.score` and
// `prediction.totalPoints` on, and the one its optimizer plans against (`_score_band` with
// `band_range_buffer_pct`). A plan delta has to be scored on that ladder to add up with
// `totalPoints`: on the published CMS ladder a buffered department sitting between its two
// ladders would read a backend free cut (which stops at the BUFFERED floor) as a CMS band drop.
// Identical to the CMS ladder when the buffer is 0. The band walk itself lives in
// `bandBuffer.utils` so the FE has exactly one mirror of the backend `_band_matches`.
export const pointsForHppd = (cat: PbjCategoryScore, rawValue: number): number => bandPointsForValue(cat, rawValue, "effective");

// Total points per raw measure point — the FE mirror of the backend `scoring._rescale_factor`.
// When a measure is N/A, CMS rescales the remaining measures' raw sum onto the full scale
// (`total = raw × maxPoints / availableMaxPoints`), and the backend multiplies every step's points
// by the same factor. Band points from this file meet `prediction.totalPoints` only after this
// multiplier, or a plan's "+N pts" would be in raw units next to a rescaled total.
export const planPointsScale = (prediction: Pick<PbjPrediction, "rescaled" | "maxPoints" | "availableMaxPoints">): number => {
	if (!prediction.rescaled) return 1;
	// A rescale with nothing left to score is a broken contract: dividing by it would turn every
	// plan figure into Infinity/NaN instead of failing where the bad data arrived.
	if (prediction.availableMaxPoints <= 0) throw new Error(`Rescaled prediction has no available points (${prediction.availableMaxPoints})`);
	return prediction.maxPoints / prediction.availableMaxPoints;
};

// FE analog of the backend `_SEQUENTIAL_BAND_EPSILON` in
// `backend/services/pbj_model/greedy.py::_to_steps`. A stored gap was computed
// without the greedy's per-iteration float nudges, so `baseline + gap` lands a
// few microsteps SHY of the band cutoff the greedy actually crossed (e.g.
// 0.677995 against a 0.678 cutoff). This epsilon nudges the value strictly
// inside the next half-open `[min, max)` band so re-scoring stays in step with
// the backend. Kept identical to the backend constant (1e-4): large enough to
// cover cumulative float drift, far smaller than the narrowest band (> 0.07).
export const SEQUENTIAL_BAND_EPSILON = 1e-4;

// SIGNED marginal RAW measure points a single per-category `hppdGap` earns over `before`
// (multiply by `planPointsScale` before comparing with a total), plus the
// advanced baseline: positive for an add crossing a band ceiling, negative for a cut
// crossing a band floor, 0 when the value stays in its band. No clamp — a cut's lost
// points must reach the plan total, exactly like the backend's unclamped signed
// `_to_steps` (`pts = post − pre`). The nudge is applied BEFORE scoring `after` —
// mirroring the backend (`running_raw += gap + EPS` first, THEN re-score) — and points
// the same way (+EPS) for both signs: on an add it credits a BE-suggested gap landing a
// hair below its band cutoff; on a cut it errs toward FEWER points lost, so a cut the
// backend parked a hair above a floor never reads as crossing it. Scoring `before + gap`
// raw and only nudging the NEXT item's baseline
// under-credited each such crossing by one band (DAT-497: the Action Plan read
// "Need 5 more points" while the BE plan had met the target). This is the single
// shared scorer for every points figure derived from a stored gap — the
// cumulative pass below and the sidebar's per-rec previews.
export const scoreGapFromBaseline = (cat: PbjCategoryScore, before: number, hppdGap: number): { gain: number; after: number } => {
	const after = before + hppdGap + SEQUENTIAL_BAND_EPSILON;
	return { gain: pointsForHppd(cat, after) - pointsForHppd(cat, before), after };
};

// HPPD categories the recommendation levers couple to ("1"=Total, "2"=RN,
// "3"=Weekend). Turnover/Admin categories are never advanced by a plan item.
const HPPD_CATEGORY_IDS = ["1", "2", "3"] as const;
const HPPD_CATEGORY_ID_SET = new Set<string>(HPPD_CATEGORY_IDS);

// The Adjusted Weekend HPPD category id (mirror of the BE `scoring.py` category ids).
// Weekend measures/recs use the weekend-only day denominator; everything else uses the
// full calendar-day denominator. Centralized here so the HPPD/day-denominator math in
// recMath, CategoryBreakdownTable, and ManageManualHoursDialog reads from one constant
// instead of re-hardcoding "3".
export const WEEKEND_CATEGORY_ID = "3";

// The Adjusted RN HPPD category id (mirror of the BE `scoring.py` category ids) — the measure an RN
// lever moves, and the one the federal RN-on-duty floor protects.
export const RN_CATEGORY_ID = "2";

// Days/week each staffing change lands on: weekend-only recs move hours on Sat+Sun (2),
// every-day recs move hours across all 7. Shared by ActionItems, CategoryBreakdownTable,
// and planOutcome so the per-week hours conversion is defined once.
export const DAYS_PER_WEEK_WEEKEND = 2;
export const DAYS_PER_WEEK_FULL = 7;

// Per-item result of the cumulative pass.
export type CumulativeItemResult = {
	// Signed marginal points this item earned (add) or gave up (cut) over the prefix of
	// items added before it.
	points: number;
	// Per-category marginal points, keyed by category id, so ActionItems' breakdown
	// sub-rows sum to `points`.
	pointsByCategory: Record<string, number>;
};

export type CumulativePassResult = {
	// Per-item marginal points keyed by PlanItem id.
	byItemId: Record<string, CumulativeItemResult>;
	// Sum of every item's signed marginal points — the plan's true cumulative change
	// (negative when its cuts give up more than its adds earn).
	total: number;
};

// Seed a `running` per-category adjusted-HPPD map from each HPPD category's
// `rawValue`. Categories without a band ladder (turnover/admin) are skipped since
// plan items never touch them.
const seedRunning = (categories: PbjCategoryScore[]): Record<string, number> => {
	const running: Record<string, number> = {};
	for (const cat of categories) {
		if (HPPD_CATEGORY_ID_SET.has(cat.id)) {
			running[cat.id] = cat.rawValue;
		}
	}
	return running;
};

// Index HPPD categories by id for O(1) lookup during the walk.
const indexHppdCategories = (categories: PbjCategoryScore[]): Record<string, PbjCategoryScore> => {
	const byId: Record<string, PbjCategoryScore> = {};
	for (const cat of categories) {
		if (HPPD_CATEGORY_ID_SET.has(cat.id)) byId[cat.id] = cat;
	}
	return byId;
};

// Advance `running` by a single plan item's signed per-category HPPD gaps, mutating the
// map in place. Returns the signed marginal points that item earns over the current
// `running` baseline (the prefix of items already applied). Mirrors the backend
// greedy's `_to_steps` sequential attribution: each touched category is scored
// via `scoreGapFromBaseline` (nudge first, then re-score — see that helper), and
// `running` advances to the same nudged value the points were scored at.
const applyItem = (item: PlanItem, byId: Record<string, PbjCategoryScore>, running: Record<string, number>, pointsScale: number): CumulativeItemResult => {
	const pointsByCategory: Record<string, number> = {};
	let points = 0;
	// DEFAULT: an item without a stored breakdown can't be scored cumulatively (shouldn't
	// happen once every add/drag attaches one) — it contributes 0 and leaves running.
	const breakdown: PlanItemCategoryBreakdown[] = item.breakdown ?? [];
	for (const bucket of breakdown) {
		const cat = byId[bucket.categoryId];
		if (!cat) continue;
		const { gain: rawGain, after } = scoreGapFromBaseline(cat, running[bucket.categoryId], bucket.hppdGap);
		const gain = rawGain * pointsScale;
		pointsByCategory[bucket.categoryId] = gain;
		points += gain;
		running[bucket.categoryId] = after;
	}
	return { points, pointsByCategory };
};

// Cumulative-scoring pass — the FE analog of `backend/services/pbj_model/greedy.py
// ::_to_steps`. Walks `selectedPlan` in add-order, crediting each item the signed
// marginal point change over the items added BEFORE it. Because band scoring is a
// nonlinear step function, this is the only attribution that (a) never under-credits (a
// later item scores from the HPPD already moved by earlier items) and (b) never
// double-credits (two items crossing the same band edge split the points by add
// order). The total is invariant to how the change is split across items. Mixed plans
// work the same way: a cut scored after an add sees the add's raised baseline, so a cut
// that only gives back part of an add's surplus loses 0 points. Automatic suggestions contain
// only additions; the signed ordering here remains for user-assembled Action Plans.
// `pointsScale` (see `planPointsScale`) turns raw measure points into total points, so every
// figure this returns is in the same units as `prediction.totalPoints`.
export const computeCumulativePass = (selectedPlan: PlanItem[], categories: PbjCategoryScore[], pointsScale: number): CumulativePassResult => {
	const byId = indexHppdCategories(categories);
	const running = seedRunning(categories);
	const byItemId: Record<string, CumulativeItemResult> = {};
	let total = 0;
	for (const item of selectedPlan) {
		const result = applyItem(item, byId, running, pointsScale);
		byItemId[item.id] = result;
		total += result.points;
	}
	return { byItemId, total };
};

// Effective per-category baseline `running` map after applying a PREFIX of
// `selectedPlan` — every item strictly before `stopId`, or the entire plan when
// `stopId` is undefined (a not-yet-selected rec is scored against everything
// currently in the plan). The sidebar uses this so a rec's "+X pts" preview scores
// against `rawValue + cumulative shift of the items added before it`, making the
// preview equal that rec's Action Plan row.
export const effectiveBaselineForPrefix = (selectedPlan: PlanItem[], categories: PbjCategoryScore[], stopId?: PlanItem["id"]): Record<string, number> => {
	const byId = indexHppdCategories(categories);
	const running = seedRunning(categories);
	for (const item of selectedPlan) {
		if (stopId !== undefined && item.id === stopId) break;
		// Only the advanced baseline is read here, never the points, so the scale is irrelevant.
		applyItem(item, byId, running, 1);
	}
	return running;
};
